import { useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Buildings } from "@phosphor-icons/react";

/* Quick "one client's work" filter for list pages. Groups loaded rows by client_id (falling back to the typed client
   name for unlinked records) and shows a count per client. Value: "all" | "id:<client_id>" | "name:<lowercase name>". */

export function clientKey(row) {
  if (row?.client_id) return `id:${row.client_id}`;
  const name = (row?.client_company || row?.client_name || "").trim();
  return name ? `name:${name.toLowerCase()}` : "";
}

export function clientLabel(row) {
  return (row?.client_company || row?.client_name || "").trim();
}

export function matchesClientFilter(row, value) {
  return !value || value === "all" || clientKey(row) === value;
}

/* URL param helpers so a filtered list can be deep-linked (e.g. from a client's page): ?client_id=… or ?client=<name> */
export function clientFilterFromParams(params) {
  const id = params.get("client_id");
  if (id) return `id:${id}`;
  const name = params.get("client");
  return name ? `name:${name.trim().toLowerCase()}` : "all";
}

export function applyClientFilterParam(params, value) {
  const next = new URLSearchParams(params);
  next.delete("client_id"); next.delete("client");
  if (value && value !== "all") {
    if (value.startsWith("id:")) next.set("client_id", value.slice(3));
    else next.set("client", value.slice(5));
  }
  return next;
}

export default function ClientFilterSelect({ rows, value, onChange, className = "", testid = "client-filter", placeholder = "All Clients" }) {
  const options = useMemo(() => {
    const map = new Map();
    for (const r of rows || []) {
      const key = clientKey(r);
      if (!key) continue;
      const cur = map.get(key) || { key, label: clientLabel(r) || "Unnamed client", count: 0, hasName: false };
      cur.count += 1;
      if (!cur.hasName && clientLabel(r)) { cur.label = clientLabel(r); cur.hasName = true; }
      map.set(key, cur);
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);
  const current = options.find((o) => o.key === value);
  const stale = value !== "all" && !current; // e.g. deep-linked client with no rows in this list
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className} data-testid={testid} title="Show one client's work">
        <Buildings size={14} className="mr-1 shrink-0 text-muted-foreground" />
        <SelectValue placeholder={placeholder}>{value === "all" ? placeholder : (current?.label || (stale ? "Client (no matches)" : ""))}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid={`${testid}-all`}>{placeholder}</SelectItem>
        {stale && <SelectItem value={value} data-testid={`${testid}-stale`}>Client (no matches)</SelectItem>}
        {options.map((o) => (
          <SelectItem key={o.key} value={o.key} data-testid={`${testid}-option`}>
            <span className="flex items-center justify-between gap-3 w-full"><span className="truncate">{o.label}</span><span className="text-[10px] text-muted-foreground tabular-nums">{o.count}</span></span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
